import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import {
  getAppSettingsData,
  type AppSettingsNormalized,
  type MenuWordEntity,
  type SettingMenuButtonEntity,
} from "~/utils/receptionDB";
import { ButtonType, CallType, type ChatRequestType } from "~/utils/receptionDB.types";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import ReceptionHeader from "~/components/ReceptionHeader";
import ReceptionKeypadButton from "~/components/ReceptionKeypadButton";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import * as icon from "~/Icons";

import { languageAtom, receptionButtonTypeAtom, receptionCompanyIdAtom } from "~/Atom";

import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { runReceptionBack } from "~/utils/receptionNavigation";
import { ROUTE } from "~/constants/routes";
import "~/css/reception/common.css";
import "~/css/reception/ReceptionNumericInputPage.css";

type VisitorInfoNavigationState = {
  companyName?: string;
  visitorName?: string;
  requirement?: string;
  chatRequestType?: ChatRequestType;
  callType?: string;
  extensionNumber?: string;
  faceTimeAddress?: string;
  notificationType?: string;
  buttonTitle?: string;
};

const getMenuButtonConfig = (
  buttons: SettingMenuButtonEntity[],
  groupDisplayId?: number
): SettingMenuButtonEntity | undefined => {
  if (groupDisplayId !== undefined) {
    const matched = buttons.find((button) => button.id === groupDisplayId);
    if (matched) return matched;
  }

  return buttons.find((button) => button.id === 0) ?? buttons[0];
};

const resolveNotificationType = (
  buttonType: string,
  config: SettingMenuButtonEntity
): string | undefined => {
  const notificationKeyByType: Partial<Record<ButtonType, keyof SettingMenuButtonEntity>> = {
    [ButtonType.RECRUIT]: "recruitNotificationType",
    [ButtonType.OTHER]: "otherNotificationType",
    [ButtonType.SECRETARY]: "secretaryNotificationType",
  };

  const key = notificationKeyByType[buttonType as ButtonType];
  return key ? String(config[key]) : undefined;
};

const resolveCallType = (
  buttonType: string,
  config: SettingMenuButtonEntity
): string | undefined => {
  const callTypeKeyByType: Partial<Record<ButtonType, keyof SettingMenuButtonEntity>> = {
    [ButtonType.RECRUIT]: "recruitCallType",
    [ButtonType.OTHER]: "otherCallType",
    [ButtonType.SECRETARY]: "secretaryCallType",
  };

  const key = callTypeKeyByType[buttonType as ButtonType];
  return key ? String(config[key]) : undefined;
};

const resolveButtonTitle = (
  menuWordEntities: MenuWordEntity[],
  buttonType: string,
  languageCode: string,
  groupDisplayId?: number
): string | undefined => {
  const targetId = groupDisplayId ?? 0;
  const menuWordEntity =
    menuWordEntities.find((entity) => entity.id === targetId) ??
    menuWordEntities.find((entity) => entity.id === 0) ??
    menuWordEntities[0];

  if (!menuWordEntity || menuWordEntity.items.length === 0) return undefined;

  const normalizedLanguage = languageCode.toLowerCase();
  const localizedMenuWord =
    menuWordEntity.items.find((item) =>
      normalizedLanguage.startsWith(item.languageCode.toLowerCase())
    ) ?? menuWordEntity.items[0];

  if (!localizedMenuWord) return undefined;

  type MenuButtonTitleKey =
    | "groupButtonTitle"
    | "userButtonTitle"
    | "recruitButtonTitle"
    | "deliveryButtonTitle"
    | "taxiButtonTitle"
    | "secretaryButtonTitle"
    | "otherButtonTitle"
    | "qrCodeButtonTitle"
    | "receptionNumberButtonTitle"
    | "faceRecognitionButtonTitle";

  const titleKeyByType: Partial<Record<ButtonType, MenuButtonTitleKey>> = {
    [ButtonType.GROUP]: "groupButtonTitle",
    [ButtonType.USER]: "userButtonTitle",
    [ButtonType.RECRUIT]: "recruitButtonTitle",
    [ButtonType.DELIVERY]: "deliveryButtonTitle",
    [ButtonType.TAXI]: "taxiButtonTitle",
    [ButtonType.SECRETARY]: "secretaryButtonTitle",
    [ButtonType.OTHER]: "otherButtonTitle",
    [ButtonType.QR]: "qrCodeButtonTitle",
    [ButtonType.RECEPTIONCODE]: "receptionNumberButtonTitle",
    [ButtonType.FACERECOGNITION]: "faceRecognitionButtonTitle",
  };

  const key = titleKeyByType[buttonType as ButtonType];
  return key ? localizedMenuWord.menuWords[key] : undefined;
};

const normalizeOptionValue = (value: string | undefined): string | undefined => {
  const normalizedValue = value?.trim().toUpperCase();
  return normalizedValue ? normalizedValue : undefined;
};

const buildContactFields = (
  callType: string | undefined,
  value: string
): Pick<VisitorInfoNavigationState, "extensionNumber" | "faceTimeAddress"> => {
  if (callType === CallType.Phone) {
    return { extensionNumber: value, faceTimeAddress: undefined };
  }

  if (callType === CallType.FaceTime) {
    return { extensionNumber: undefined, faceTimeAddress: value };
  }

  return { extensionNumber: undefined, faceTimeAddress: undefined };
};

const computeForwardState = (
  settings: AppSettingsNormalized,
  currentState: VisitorInfoNavigationState,
  buttonType: string,
  groupDisplayId: number | undefined,
  value: string,
  language?: string
) => {
  const config = getMenuButtonConfig(settings.menuButtons, groupDisplayId);
  if (!config) return currentState;

  const normalizedCallType = normalizeOptionValue(resolveCallType(buttonType, config));
  const normalizedNotificationType = normalizeOptionValue(
    resolveNotificationType(buttonType, config)
  );
  const resolvedCallType = currentState.callType ?? normalizedCallType;
  const trimmedValue = value.trim();

  return {
    ...currentState,
    chatRequestType: currentState.chatRequestType ?? "noRequest",
    callType: currentState.callType ?? normalizedCallType,
    notificationType: currentState.notificationType ?? normalizedNotificationType,
    buttonTitle:
      currentState.buttonTitle ??
      resolveButtonTitle(settings.menuWords, buttonType, language ?? "ja", groupDisplayId),
    ...buildContactFields(resolvedCallType, trimmedValue),
  } as VisitorInfoNavigationState;
};

/**
 * 受付時の内線番号入力を行い、入力内容に応じて通話画面へ遷移する画面。
 * あわせて、音声案内や遷移前後で利用する状態・タイマー退避の前提となる処理を担う。
 */
const InputExtensionNumberPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [value, setValue] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [language] = useAtom(languageAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [receptionButtonType] = useAtom(receptionButtonTypeAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  const appSettingsRef = useRef<AppSettingsNormalized | null>(null);
  const appSettingsLoadedRef = useRef(false);
  const appSettingsRequestRef = useRef<Promise<AppSettingsNormalized | undefined> | null>(null);

  const getCachedAppSettings = useCallback(async () => {
    if (appSettingsLoadedRef.current) {
      return appSettingsRef.current ?? undefined;
    }
    if (appSettingsRequestRef.current) {
      return appSettingsRequestRef.current;
    }

    appSettingsRequestRef.current = getAppSettingsData()
      .then((settings) => {
        appSettingsRef.current = settings ?? null;
        appSettingsLoadedRef.current = true;
        return settings;
      })
      .finally(() => {
        appSettingsRequestRef.current = null;
      });

    return appSettingsRequestRef.current;
  }, []);

  const resolveForwardState = useCallback(async () => {
    const settings = await getCachedAppSettings();
    const currentState = ((location.state as VisitorInfoNavigationState | null) ??
      {}) as VisitorInfoNavigationState;

    if (!settings) return currentState;

    const buttonType = (receptionButtonType ?? "").trim().toUpperCase();
    const groupDisplayId = receptionCompanyId;
    const menuButton = getMenuButtonConfig(settings.menuButtons, groupDisplayId);
    if (!buttonType || !menuButton) return currentState;

    return computeForwardState(settings, currentState, buttonType, groupDisplayId, value, language);
  }, [
    getCachedAppSettings,
    language,
    location.state,
    receptionButtonType,
    receptionCompanyId,
    value,
  ]);

  useEffect(() => {
    void playWithCooldown(t("titleInputExtensionNumber")).catch((error) => {
      console.error("inputExtensionNumber playVoice failed:", error);
    });
  }, [playWithCooldown]);

  useInactivityTimeout(navigate);

  const handleNumberClick = useCallback((num: string) => {
    setValue((prev) => prev + num);
  }, []);

  const handleDelete = useCallback(() => {
    setValue((prev) => prev.slice(0, -1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!value.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const nextForwardState = await resolveForwardState();

      navigate(ROUTE.RECEPTION.CALLING, {
        state: nextForwardState,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [value, isSubmitting, resolveForwardState, navigate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key >= "0" && event.key <= "9") {
        event.preventDefault();
        handleNumberClick(event.key);
        return;
      }

      if (event.key === "Backspace") {
        event.preventDefault();
        handleDelete();
        return;
      }

      if (event.key === "Enter" && value.trim()) {
        event.preventDefault();
        void handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [value, handleNumberClick, handleDelete, handleSubmit]);

  const handleBack = useCallback(() => {
    runReceptionBack({ kind: "router-history", navigate });
  }, [navigate]);

  const isValid = value.trim().length > 0;
  const deleteLabel = t("delete");
  const okLabel = t("ok");

  return (
    <div className="extension-number-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />

      <main className="extension-number-screen">
        <div className="screen__text extension-number-screen__text">
          <h2 className="screen__title">{t("titleInputExtensionNumber")}</h2>
        </div>

        <div className="extension-number-screen__body">
          <div className="extension-number-display-box" aria-live="polite">
            {value ? (
              <span className="extension-number-display-box__value">{value}</span>
            ) : (
              <span className="extension-number-display-box__placeholder">
                {t("placeholderExtensionNumber")}
              </span>
            )}
          </div>

          <div className="extension-number-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <ReceptionKeypadButton
                key={n}
                ariaLabel={String(n)}
                label={String(n)}
                onClick={() => handleNumberClick(String(n))}
              />
            ))}

            <ReceptionKeypadButton ariaLabel="0" label="0" onClick={() => handleNumberClick("0")} />

            <ReceptionKeypadButton ariaLabel={deleteLabel} onClick={handleDelete}>
              <icon.icon_delete className="reception-keypad-button__icon" aria-hidden="true" />
            </ReceptionKeypadButton>

            <ReceptionKeypadButton
              ariaLabel={okLabel}
              disabled={!isValid || isSubmitting}
              label={okLabel}
              onClick={() => {
                void handleSubmit();
              }}
              variant="primary"
            />
          </div>

          {isSubmitting && <ReceptionCommunicatingIndicator />}
        </div>
      </main>
    </div>
  );
};

export default InputExtensionNumberPage;
